'use client'

import React from 'react'
import clsx from 'classnames'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { Text } from '@/shared/ui'
import { Dialog, DialogTrigger, DialogContent, DialogClose } from '@/sections/search/dialog/dialog'

import { useAppearance } from '@/shared/utils'
import { variantsHeadline } from '@/shared/constants/variants'
import style from './photo.module.scss'

interface PhotoProps extends React.HTMLProps<HTMLDivElement> {
  src: string
  alt: string
  description: string
  author: string
  hasFullscreenMode: boolean
}

export const Photo = (props: PhotoProps) => {
  const { className, src, alt, description, author, hasFullscreenMode } = props

  const { ref: innerRef, animate } = useAppearance()

  return (
    <motion.div
      ref={innerRef}
      animate={animate}
      variants={variantsHeadline}
      className={clsx(style.container, className)}
    >
      {hasFullscreenMode ? (
        <Dialog>
          <DialogTrigger asChild>
            <div className={style.photo} style={{ cursor: 'zoom-in', position: 'relative' }}>
              <Image
                src={src}
                alt={alt ?? 'alt'}
                width={720}
                height={480}
                style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
              />
              <button className={style.zoomBtn} aria-label="Открыть фото в полный экран">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M0 1.71399V7.71429C0 8.06097 0.208836 8.37351 0.529128 8.50618C0.84942 8.63885 1.21809 8.56551 1.46323 8.32037L3.67963 6.10399L7.35924 9.78362C8.02872 10.4531 9.11414 10.4531 9.78362 9.78362C10.4531 9.11414 10.4531 8.02872 9.78362 7.35924L6.10399 3.67963L8.32037 1.46323C8.56551 1.21809 8.63885 0.84942 8.50618 0.529128C8.37351 0.208836 8.06097 0 7.71429 0H1.71458H0.857143C0.383757 0 0 0.383757 0 0.857143V1.71399ZM9.78362 16.6408C10.4531 15.9713 10.4531 14.8859 9.78362 14.2164C9.11414 13.5469 8.02872 13.5469 7.35924 14.2164L3.67963 17.8959L1.46323 15.6796C1.21809 15.4345 0.84942 15.3611 0.529128 15.4938C0.208836 15.6265 0 15.939 0 16.2857V22.2854V23.1429C0 23.6162 0.383757 24 0.857143 24H1.7131H1.71547H7.71429C8.06097 24 8.37351 23.7912 8.50618 23.4708C8.63885 23.1506 8.56551 22.7818 8.32037 22.5367L6.10399 20.3205L9.78362 16.6408ZM14.2164 14.2164C14.8859 13.5469 15.9713 13.5469 16.6408 14.2164L20.3205 17.8959L22.5367 15.6796C22.7818 15.4345 23.1506 15.3611 23.4708 15.4938C23.7912 15.6265 24 15.939 24 16.2857V22.2845V22.2869V23.1429C24 23.6162 23.6162 24 23.1429 24H22.2869H22.2845H16.2857C15.939 24 15.6265 23.7912 15.4938 23.4708C15.3611 23.1506 15.4345 22.7818 15.6796 22.5367L17.8959 20.3205L14.2164 16.6408C13.5469 15.9713 13.5469 14.8859 14.2164 14.2164ZM16.2857 0C15.939 0 15.6265 0.208836 15.4938 0.529128C15.3611 0.84942 15.4345 1.21809 15.6796 1.46323L17.8959 3.67963L14.2164 7.35924C13.5469 8.02872 13.5469 9.11414 14.2164 9.78362C14.8859 10.4531 15.9713 10.4531 16.6408 9.78362L20.3205 6.10399L22.5367 8.32037C22.7818 8.56551 23.1506 8.63885 23.4708 8.50618C23.7912 8.37351 24 8.06097 24 7.71429V1.71547V1.7131V0.857143C24 0.383757 23.6162 0 23.1429 0H22.2861H16.2857Z"
                  />
                </svg>
              </button>
            </div>
          </DialogTrigger>
          <DialogContent className={style.fullscreenModal}>
            <DialogClose className={style.closeBtn} aria-label="Закрыть полноэкранное фото">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="8" y1="8" x2="24" y2="24" stroke="#fff" strokeWidth="2" />
                <line x1="24" y1="8" x2="8" y2="24" stroke="#fff" strokeWidth="2" />
              </svg>
            </DialogClose>
            <div className={style.fullscreenImgWrap}>
              <Image
                src={src}
                alt={alt ?? 'alt'}
                width={1280}
                height={800}
                style={{ objectFit: 'contain', minHeight: '100%', height: 'fit-content', maxWidth: '100%' }}
              />
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <div className={style.photo}>
          <Image
            src={src}
            alt={alt ?? 'alt'}
            width={720}
            height={480}
            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
          />
        </div>
      )}
      <Text className={style.description} type="t2" color="dark" __html={description} />
      <Text className={style.author} type="t2" color="dark-040" __html={author} />
    </motion.div>
  )
}
